import { Injectable } from '@angular/core';
import html2canvas from 'html2canvas';
import { LogConfig } from '@interface/IPdfGeneration';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { BehaviorSubject } from 'rxjs';
import { CellDef, VAlignType, HAlignType } from 'jspdf-autotable';

@Injectable({
  providedIn: 'root',
})
export class PdfViewerService {
  private pdfParams: any = {};
  private pdfBlob: Blob | null = null;
  private previousUrl: string | null = null;

  private progressMessage = new BehaviorSubject<string>('');
  progressMessage$ = this.progressMessage.asObservable();

  updateProgressMessage(val: string) {
    this.progressMessage.next(val);
  }

  constructor() {}
  setPdfData(data: Blob | null) {
    this.pdfBlob = data;
  }

  getPdfData(): Blob | null {
    return this.pdfBlob;
  }

  getPdfParameters() {
    return this.pdfParams;
  }

  createBannerContent(title: string): HTMLElement {
    const bannerDiv = document.createElement('div');
    bannerDiv.id = 'bannerDiv';
    bannerDiv.style.cssText =
      'background-color: #a32424; color: #ffffff; text-align: left; font-size: 10px; font-weight: bold;';
    bannerDiv.textContent = title;
    return bannerDiv;
  }

  createHeader(
    selectedDealer: any,
    bannerDiv: HTMLElement,
    title: string,
  ): HTMLElement {
    const headerContainer = document.createElement('div');
    headerContainer.style.display = 'flex';
    headerContainer.style.alignItems = 'center';
    headerContainer.style.justifyContent = 'space-between';
    headerContainer.style.width = '100%';
    headerContainer.style.minWidth = '800px';
    headerContainer.style.backgroundColor = '#A32424';
    headerContainer.style.paddingRight = '20px';
    const logoWrapper = document.createElement('div');
    logoWrapper.style.display = 'flex';
    logoWrapper.style.alignItems = 'center';

    const logoDetails: LogConfig = {
      src: 'assets/images/logo.webp',
      height: '45px',
      marginRight: '0px',
      textContent: '',
    };
    const logoDiv = this.LogoContent(logoDetails);
    if (logoDiv.length > 0) {
      logoWrapper.append(...logoDiv);
    }
    // Dealer Info
    const dealerInfoDiv = document.createElement('div');
    dealerInfoDiv.innerHTML = `<span style="color: #fff; font-size: 18px; font-weight: bold;">${selectedDealer?.orgNickName || ''}</span>`;
    dealerInfoDiv.style.marginLeft = '10px';
    // Report Title
    const reportTitleDiv = document.createElement('div');
    reportTitleDiv.innerHTML = `<span style="color: #fff; font-size: 25px; font-weight: bold;">${title}</span>`;
    logoWrapper.appendChild(dealerInfoDiv);
    headerContainer.appendChild(logoWrapper);
    headerContainer.appendChild(reportTitleDiv);
    bannerDiv.innerHTML = '';
    bannerDiv.appendChild(headerContainer);
    bannerDiv.classList.add('banner');
    return bannerDiv;
  }

  LogoContent(logoContent: LogConfig): HTMLElement[] {
    const topRowDiv = document.createElement('div');
    topRowDiv.style.display = 'flex';
    topRowDiv.style.alignItems = 'center';

    // Create the logo div
    const logo = document.createElement('img');
    logo.src = logoContent.src ?? '';
    logo.alt = logoContent.alt ?? '';
    logo.style.height = logoContent.height ?? '';
    logo.style.width = logoContent.width ?? '';
    logo.style.marginRight = logoContent.marginRight ?? '';

    const groupInfoSpan = document.createElement('span');
    groupInfoSpan.style.marginRight = '10px';
    groupInfoSpan.textContent = logoContent.textContent ?? '';
    topRowDiv.appendChild(logo);
    topRowDiv.appendChild(groupInfoSpan);

    const rightSideDiv = document.createElement('div');
    rightSideDiv.style.display = 'block';
    rightSideDiv.style.fontSize = '18px';
    rightSideDiv.style.fontWeight = 'bold';
    rightSideDiv.textContent = logoContent.title ?? '';

    return [topRowDiv, rightSideDiv];
  }

  FilterContent() {
    const filterContentDiv = document.createElement('div');
    Object.assign(filterContentDiv.style, {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '0px',
      borderBottom: '1px solid #000',
      fontSize: '15px',
    });
    return filterContentDiv;
  }

  async generatePdfForGraphs(
    mainDiv: HTMLElement,
    orientation: 'portrait' | 'landscape',
    pageSize: string,
  ): Promise<Blob | null> {
    try {
      if (!mainDiv) {
        console.error('❌ Error: mainDiv is null or undefined.');
        return null;
      }

      // Ensure full width capture
      mainDiv.style.width = 'max-content';
      mainDiv.style.overflow = 'visible';

      // Wait for layout to adjust
      await new Promise((resolve) => setTimeout(resolve, 300));

      if (!document.body.contains(mainDiv)) {
        return null;
      }

      // Ensure all images are fully loaded
      const images = mainDiv.getElementsByTagName('img');
      const imagePromises = Array.from(images).map(
        (img) =>
          new Promise((resolve) => {
            if (img.complete) resolve(true);
            img.onload = () => resolve(true);
            img.onerror = () => {
              console.error('❌ Image failed to load:', img.src);
              resolve(false);
            };
          }),
      );
      await Promise.all(imagePromises);

      // Capture with html2canvas
      const canvas = await html2canvas(mainDiv, {
        scale: 2, // Better quality
        scrollX: 0,
        scrollY: 0,
        windowWidth: mainDiv.scrollWidth + 50, // Full width capture
        windowHeight: mainDiv.scrollHeight,
        width: mainDiv.scrollWidth, // Force full width capture
        useCORS: true,
        allowTaint: true,
      });

      // PDF Setup
      const pdf = new jsPDF(orientation, 'mm', pageSize);
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const pageMarginBottom = 20; // Space for footer
      const usablePageHeight = pageHeight - pageMarginBottom; // Content space

      const imgWidth = pageWidth - 20; // Leave margins
      const pageCanvasHeight = (canvas.width * usablePageHeight) / imgWidth; // Adjusted height

      let y = 0;
      while (y < canvas.height) {
        const croppedCanvas = document.createElement('canvas');
        const croppedCtx = croppedCanvas.getContext('2d');

        croppedCanvas.width = canvas.width;
        croppedCanvas.height = Math.min(pageCanvasHeight, canvas.height - y);

        if (croppedCtx) {
          croppedCtx.drawImage(
            canvas,
            0,
            y,
            canvas.width,
            croppedCanvas.height,
            0,
            0,
            canvas.width,
            croppedCanvas.height,
          );
        }

        const croppedImgData = croppedCanvas.toDataURL('image/png');
        if (y > 0) pdf.addPage(); // New page if necessary
        pdf.addImage(
          croppedImgData,
          'PNG',
          10,
          10,
          imgWidth,
          (croppedCanvas.height * imgWidth) / canvas.width,
        );

        y += croppedCanvas.height;
      }

      // Add page numbers correctly
      const totalPages = pdf.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);

        // Position the page number inside the footer margin
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 5, {
          align: 'center',
        });
      }

      // Generate PDF Blob
      const pdfBlob = pdf.output('blob');

      // Reset styles
      mainDiv.style.width = '';
      mainDiv.style.overflow = '';

      return pdfBlob;
    } catch {
      return null;
    }
  }

  setPreviousUrl(url: string): void {
    this.previousUrl = url;
    localStorage.setItem('pdfUrl', this.previousUrl);
  }

  getPreviousUrl(): string | null {
    return this.previousUrl;
  }

  // NEW PDF EXPORT SERVICE METHODS BELOW
  async generatePdfFromTable(
    mainDiv: HTMLElement,
    orientation: 'portrait' | 'landscape',
    pageSize: string,
    reportTitle: string,
    dealerName: string,
    scale: number = 1,
  ): Promise<Blob | null> {
    try {
      this.updateProgressMessage('Generating PDF...');
      const pdf = new jsPDF(orientation, 'mm', pageSize);
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let currentY = 10;

      /* ---------------- Banner ---------------- */
      this.updateProgressMessage('Generating banner section...');
      currentY = await this.drawBannerWithJsPdf(
        pdf,
        reportTitle,
        dealerName,
        pageWidth,
        currentY,
      );

      /* ---------------- Filters ---------------- */
      if (mainDiv.querySelector('#filtersDiv')) {
        this.updateProgressMessage('Adding filters section...');
        const filtersDiv = mainDiv.querySelector('#filtersDiv') as HTMLElement;
        if (filtersDiv) {
          currentY = this.drawFiltersWithJsPdf(
            pdf,
            filtersDiv,
            pageWidth,
            currentY,
          );
        }
      }

      /* ---------------- TABLE (FAST PART) ---------------- */
      if (mainDiv.querySelector('#contentDiv')) {
        this.updateProgressMessage('Adding table...');
        const tableSource = document.getElementById('pdf-table-content');
        tableSource!
          .querySelectorAll(
            '.search, .search-icon, input[type="text"], .icon-container, .td-icon',
          )
          .forEach((el: any) => el.remove());
        const tableRows = await this.cloneTableWithCanvasAsImages(
          tableSource!,
          scale,
        );
        autoTable(pdf, {
          startY: currentY,
          theme: 'plain', // IMPORTANT
          styles: {
            overflow: 'linebreak',
          },
          body: tableRows?.map((row) =>
            row.map((cell) => ({
              ...cell,
              content: cell.type === 'text' ? cell.value : '',
            })),
          ),
          didParseCell: (data) => {
            const raw: any = data.cell.raw;
            data.cell.styles.font = 'helvetica';
            if (raw?.textColor) {
              data.cell.styles.textColor = raw.textColor;
            }
          },
          didDrawCell: (data) => {
            const raw = data.cell.raw;
            if (!this.hasImageCell(raw)) return;

            const padding = 2;
            const maxW = data.cell.width - padding * 2;
            const maxH = data.cell.height - padding * 2;

            const imgW = raw.imageWidth;
            const imgH = raw.imageHeight;
            if (!imgW || !imgH) return;

            // Maintain aspect ratio, never stretch
            const scale = Math.min(maxW / imgW, maxH / imgH, 1);

            const drawW = imgW * scale;
            const drawH = imgH * scale;

            const x = data.cell.x + (data.cell.width - drawW) / 2;
            const y = data.cell.y + (data.cell.height - drawH) / 2;

            pdf.addImage(raw.value, 'PNG', x, y, drawW, drawH);
          },
        });
      }

      /* ---------------- Footer ---------------- */
      const totalPages = pdf.getNumberOfPages();
      const date = new Date().toLocaleString();

      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(9);
        pdf.text(
          `Page ${i} of ${totalPages} | Generated on ${date}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' },
        );
      }
      return pdf.output('blob');
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  async generatePdfFromBlocks(
    mainDiv: HTMLElement,
    orientation: 'portrait' | 'landscape',
    pageSize: string,
    reportTitle: string,
    dealerName: string,
    scale: number = 1,
  ): Promise<Blob | null> {
    try {
      this.updateProgressMessage('Generating PDF...');

      /* ---------- PDF shell ---------- */
      const pdf = new jsPDF(orientation, 'mm', pageSize);
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      let currentY = 10;

      currentY = await this.drawBannerWithJsPdf(
        pdf,
        reportTitle,
        dealerName,
        pageWidth,
        currentY,
      );

      /* ---------- Filters ---------- */
      const filtersDiv = mainDiv.querySelector('#filtersDiv') as HTMLElement;
      if (filtersDiv) {
        currentY = this.drawFiltersWithJsPdf(
          pdf,
          filtersDiv,
          pageWidth,
          currentY,
        );
      }

      /* ---------- Content ---------- */
      const contentClone = mainDiv.querySelector('#contentDiv') as HTMLElement;
      const liveContent = document.getElementById(
        'pdf-table-content',
      ) as HTMLElement;

      if (!contentClone || !liveContent) {
        console.warn('PDF content not found');
        return null;
      }

      const clonedGraphs = Array.from(
        contentClone.querySelectorAll('.graph-container'),
      ) as HTMLElement[];

      const liveGraphs = Array.from(
        liveContent.querySelectorAll('.graph-container'),
      ) as HTMLElement[];

      const baseFontSize = 12;
      const titleSpacing = 6;
      const blockSpacing = 10;

      for (let i = 0; i < clonedGraphs.length; i++) {
        const clonedGraph = clonedGraphs[i];
        const liveGraph = liveGraphs[i];

        if (!liveGraph) continue;

        /* ---------- Title ---------- */
        const title = clonedGraph
          .querySelector('.chart-title')
          ?.textContent?.trim();

        if (title) {
          pdf.setFontSize(baseFontSize * scale);
          pdf.setFont('helvetica', 'bold');
          pdf.text(title, 10, currentY);
          currentY += titleSpacing * scale;
        }

        /* ---------- Canvas ---------- */
        const canvas = liveGraph.querySelector(
          'canvas',
        ) as HTMLCanvasElement | null;

        if (canvas) {
          // Ensure chart finished rendering
          await new Promise((r) => requestAnimationFrame(r));
          await new Promise((r) => requestAnimationFrame(r));

          const img = this.chartCanvasToImage(canvas);
          const imgProps = pdf.getImageProperties(img);

          const availableWidth = pageWidth - 20;
          const drawWidth = Math.min(availableWidth, availableWidth * scale);

          const drawHeight = drawWidth / (imgProps.width / imgProps.height);

          const requiredHeight =
            (title ? titleSpacing * scale : 0) +
            drawHeight +
            blockSpacing * scale;
          /* ---------- Page break ---------- */
          if (currentY + requiredHeight > pageHeight - 20) {
            pdf.addPage();
            currentY = 20;
          }

          pdf.addImage(img, 'PNG', 10, currentY, drawWidth, drawHeight);

          currentY += drawHeight + blockSpacing * scale;
        }
      }

      /* ---------- Footer ---------- */
      const totalPages = pdf.getNumberOfPages();
      const date = new Date().toLocaleString();

      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(9);
        pdf.text(
          `Page ${i} of ${totalPages} | Generated on ${date}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' },
        );
      }

      return pdf.output('blob');
    } catch (e) {
      console.error('PDF block export failed:', e);
      return null;
    }
  }

  async generatePdfForCompositeNormal(
    mainDiv: HTMLElement,
    orientation: 'portrait' | 'landscape',
    pageSize: string,
    reportTitle: string,
    dealerName: string,
    scale: number = 1,
  ): Promise<Blob | null> {
    try {
      this.updateProgressMessage('Generating PDF...');

      const pdf = new jsPDF(orientation, 'mm', pageSize);
      const pageWidth = pdf.internal.pageSize.getWidth();
      let currentY = 10;

      /* ---------- Banner ---------- */
      currentY = await this.drawBannerWithJsPdf(
        pdf,
        reportTitle,
        dealerName,
        pageWidth,
        currentY,
      );

      /* ---------- Filters ---------- */
      const filtersDiv = mainDiv.querySelector('#filtersDiv') as HTMLElement;
      if (filtersDiv) {
        currentY = this.drawFiltersWithJsPdf(
          pdf,
          filtersDiv,
          pageWidth,
          currentY,
          false,
        );
      }

      /* ---------- Table root ---------- */
      const root = document.getElementById('pdf-table-content');
      if (!root) return null;

      const headerRow = root.querySelector('.composite-table-header-row');
      const dataRows = Array.from(
        root.querySelectorAll('.composite-table-data-row'),
      ) as HTMLElement[];
      dataRows.forEach((element) => {
        element.querySelectorAll('.no-pdf').forEach((el) => el.remove());
      });
      if (!headerRow || !dataRows.length) return null;

      /* ---------- Build table model ---------- */
      const tableRows: any[][] = [];

      const buildCell = (cell: HTMLElement) => {
        const pTags = Array.from(cell.querySelectorAll('p')) as HTMLElement[];

        // HEADER = 6 lines, DATA = 4 lines
        const expectedLines = cell.classList.contains(
          'composite-table-header-cell',
        )
          ? 6
          : 4;
        const lines: HTMLElement[] = [];

        for (let i = 0; i < expectedLines; i++) {
          lines.push(pTags[i] ?? document.createElement('p'));
        }
        const placeholder = Array(expectedLines - 1)
          .fill(' ')
          .join('\n');
        return {
          content: placeholder,
          styles: {
            ...this.getCellStyles(cell, scale),
            fillColor:
              this.getBackgroundColor(cell) ??
              this.resolveRowBackgroundColor(cell),
            overflow: 'hidden',
            valign: 'middle',
            cellPadding: {
              top: 2,
              bottom: 2,
              left: 2,
              right: 2,
            },
          },
          rawLines: lines,
        };
      };

      /* ---------- HEADER ---------- */
      const headerCells = Array.from(
        headerRow.querySelectorAll('.composite-table-header-cell'),
      ) as HTMLElement[];

      tableRows.push(headerCells.map((cell) => buildCell(cell)));

      /* ---------- DATA ROWS ---------- */
      for (const row of dataRows) {
        const cells = Array.from(
          row.querySelectorAll('.composite-table-data-cell'),
        ) as HTMLElement[];

        tableRows.push(cells.map((cell) => buildCell(cell)));
      }

      const headerCellsEls = Array.from(
        headerRow.querySelectorAll('.composite-table-header-cell'),
      ) as HTMLElement[];

      const pxToMm = (px: number) => px * 0.264583;

      const columnWidths = headerCellsEls.map((cell) =>
        pxToMm(cell.getBoundingClientRect().width),
      );

      const columnStyles: any = {};
      columnWidths.forEach((width, index) => {
        columnStyles[index] = { cellWidth: width * scale };
      });

      /* ---------- Render with autoTable ---------- */
      autoTable(pdf, {
        startY: currentY,
        theme: 'plain',
        body: tableRows,
        columnStyles,
        pageBreak: 'auto',
        rowPageBreak: 'avoid',
        styles: {
          fontSize: 9 * scale,
          lineWidth: 0.2,
          lineColor: [150, 150, 150],
          overflow: 'hidden',
        },

        didDrawCell: (data) => {
          const rawCell = data.cell.raw as any;
          if (!rawCell?.rawLines) return;

          const x = data.cell.x + 18;
          const y = data.cell.y;

          const lineHeight = 4.5 * scale; // adjust for scale
          const paddingTop = data.cell.padding('top');
          let textY = y + paddingTop;

          rawCell.rawLines.forEach((p: HTMLElement) => {
            const txt = p.textContent?.trim() ?? '';

            // Skip empty lines (still add spacing)
            if (!txt) {
              textY += lineHeight;
              return;
            }

            if (p.classList.contains('Bold')) pdf.setFont('helvetica', 'bold');
            else pdf.setFont('helvetica', 'normal');
            if (p.classList.contains('Italics'))
              pdf.setFont('helvetica', 'italic');
            if (p.classList.contains('red')) pdf.setTextColor(255, 0, 0);
            else pdf.setTextColor(10, 10, 10);
            if (p.classList.contains('Normal'))
              pdf.setFont('helvetica', 'normal');
            if (p.classList.contains('hilighted')) {
              pdf.setFillColor(246, 234, 131);
              pdf.rect(
                x - data.cell.width + data.cell.padding('left'),
                textY - 0.5,
                data.cell.width,
                lineHeight,
                'F',
              );
            }
            pdf.setFontSize(8 * scale);

            // 4️⃣ Alignment
            let align: 'left' | 'center' | 'right' = 'left';
            if (
              p.classList.contains('A') ||
              p.classList.contains('B') ||
              p.classList.contains('C') ||
              p.classList.contains('guide') ||
              p.classList.contains('col-num') ||
              p.classList.contains('D')
            )
              align = 'right';

            // 5️⃣ Draw text
            pdf.text(txt, x + 1, textY, { baseline: 'top', align });

            textY += lineHeight;
          });

          // Reset font and color after cell
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(0, 0, 0);
        },
      });

      this.drawFooter(pdf, pageWidth, pdf.internal.pageSize.getHeight());
      return pdf.output('blob');
    } catch (e) {
      console.error('Composite PDF failed', e);
      return null;
    }
  }

  private getEffectiveStyleElement(el: HTMLElement): HTMLElement {
    let current: HTMLElement | null = el;
    while (current) {
      const styles = window.getComputedStyle(current);
      if (
        styles.backgroundColor !== 'rgba(0, 0, 0, 0)' ||
        styles.fontWeight !== '400' ||
        styles.fontStyle !== 'normal' ||
        styles.color !== 'rgb(0, 0, 0)'
      ) {
        return current;
      }
      current = current.parentElement;
    }
    return el;
  }

  private getBackgroundColor(el: HTMLElement): [number, number, number] | null {
    // const bg = window.getComputedStyle(el).backgroundColor;
    const styledEl = this.getEffectiveStyleElement(el);
    const bg = window.getComputedStyle(styledEl).backgroundColor;
    if (!bg || bg === 'rgba(0, 0, 0, 0)' || bg === 'transparent') return null;
    const match = bg.match(/\d+/g);
    if (match && match.length >= 3)
      return [parseInt(match[0]), parseInt(match[1]), parseInt(match[2])];
    return null;
  }

  private drawFooter(pdf: jsPDF, pageWidth: number, pageHeight: number) {
    const totalPages = pdf.getNumberOfPages();
    const date = new Date().toLocaleString();

    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(9);
      pdf.text(
        `Page ${i} of ${totalPages} | Generated on ${date}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' },
      );
    }
  }

  private drawFiltersWithJsPdf(
    pdf: jsPDF,
    filterDiv: HTMLElement,
    pageWidth: number,
    startY: number,
    drawLine: boolean = true,
  ): number {
    const parts: string[] = [];
    Array.from(filterDiv.children).forEach((child: Element) => {
      const text = (child.textContent || '').trim();
      if (text) {
        parts.push(text.replace(/\s+/g, ' ')); // normalize spaces
      }
    });

    const filterText = parts.join('     |     ');

    const fontSize = 10;
    pdf.setFontSize(fontSize);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    const x = 10;
    const y = startY + fontSize * 0.8;

    pdf.text(filterText, x, y, { maxWidth: pageWidth - 20 });

    // bottom border
    pdf.setLineWidth(0.2);
    pdf.setDrawColor(0);
    drawLine && pdf.line(10, y + 2, pageWidth - 10, y + 2);

    return y + 6;
  }

  private async drawBannerWithJsPdf(
    pdf: jsPDF,
    title: string,
    dealerName: string,
    pageWidth: number,
    startY: number,
  ): Promise<number> {
    const bannerHeight = 20;

    // Background
    pdf.setFillColor(163, 36, 36); // #A32424
    pdf.rect(10, startY, pageWidth - 20, bannerHeight, 'F');

    // Logo
    const logo = new Image();
    logo.src = 'assets/images/logo.webp';
    await new Promise<void>((resolve) => {
      logo.onload = () => resolve();
      logo.onerror = () => resolve();
    });
    pdf.addImage(logo, 'WEBP', 14, startY + 4, 22, 12);

    // Dealer name
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text(dealerName || '', 40, startY + 9);

    // Report title
    pdf.setFontSize(16);
    pdf.text(title, pageWidth - 14, startY + 13, { align: 'right' });

    // Reset color
    pdf.setTextColor(0, 0, 0);

    return startY + bannerHeight + 1;
  }

  hasImageCell(raw: any): raw is {
    type: 'image';
    value: string;
    imageWidth: number;
    imageHeight: number;
  } {
    return raw?.type === 'image' && typeof raw.value === 'string';
  }

  async cloneTableWithCanvasAsImages(
    tableNode: HTMLElement | null,
    scale: number = 1,
  ) {
    if (!tableNode) return null;

    const rowEls = Array.from(
      tableNode.querySelectorAll('.pdf-print-row'),
    ) as HTMLElement[];

    const rows = rowEls.map((row) => Array.from(row.children) as HTMLElement[]);

    const filteredRows = this.filterNoPdfColumns(rows);
    return filteredRows.map((row) =>
      row.map((cell) => {
        const colSpan = Number(cell.getAttribute('colspan')) || 1;
        const rowSpan = Number(cell.getAttribute('rowspan')) || 1;
        const baseStyles = this.getCellStyles(cell, scale);
        if (baseStyles) {
          baseStyles.overflow = 'linebreak';
        }

        const canvas = cell.querySelector('canvas');

        if (canvas) {
          return {
            type: 'image',
            value: this.chartCanvasToImage(canvas),
            imageWidth: canvas.width * 2,
            imageHeight: canvas.height,
            styles: baseStyles,
            rowSpan,
            colSpan,
          };
        }

        const img = cell.querySelector('img') as HTMLImageElement | null;
        if (img && img.src) {
          return {
            type: 'image',
            value: this.imageElementToDataUrl(img),
            imageWidth: img.naturalWidth,
            imageHeight: img.naturalHeight,
            styles: baseStyles,
            rowSpan,
            colSpan,
          };
        }

        return {
          type: 'text',
          value: cell.innerText?.trim() ?? '',
          styles: baseStyles,
          textColor: this.resolveTextColor(cell),
          rowSpan,
          colSpan,
        };
      }),
    );
  }

  private filterNoPdfColumns(rows: HTMLElement[][]): HTMLElement[][] {
    if (!rows.length) return rows;

    const columnCount = rows[0].length;
    const hiddenColumns = new Set<number>();

    // 1️⃣ Detect columns to hide
    for (let col = 0; col < columnCount; col++) {
      for (const row of rows) {
        if (row[col]?.classList.contains('no-pdf')) {
          hiddenColumns.add(col);
          break;
        }
      }
    }

    if (!hiddenColumns.size) return rows;

    // 2️⃣ Remove those columns from all rows
    return rows.map((row) =>
      row.filter((_, index) => !hiddenColumns.has(index)),
    );
  }

  private imageElementToDataUrl(
    img: HTMLImageElement,
    width = img.naturalWidth,
    height = img.naturalHeight,
  ): string {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#ffffff'; // white background
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    return canvas.toDataURL('image/png');
  }

  private resolveTextColor(cell: HTMLElement): number[] | undefined {
    const textEl =
      cell.querySelector(
        '[style*="color"], .positive, .negative, .greenbg .td-guide-text, .td-guide-text',
      ) ??
      cell.firstElementChild ??
      cell;

    const color = window.getComputedStyle(textEl as Element).color;
    if (!color) return;

    const match = color.match(/\d+/g);
    if (!match || match.length < 3) return;

    return [Number(match[0]), Number(match[1]), Number(match[2])];
  }

  private getCellStyles(
    cell: HTMLElement,
    scale: number = 1,
  ): Partial<CellDef['styles']> {
    const styles = window.getComputedStyle(cell);

    const cellBg =
      styles.backgroundColor !== 'rgba(0, 0, 0, 0)'
        ? styles.backgroundColor
        : undefined;

    const rowBg = this.resolveRowBackgroundColor(cell);
    const rowBorder = this.resolveRowBorder(cell);
    return {
      fillColor: cellBg ?? rowBg, // 👈 key line
      fontSize: parseFloat(styles.fontSize) * 0.75 * scale,
      halign: styles.textAlign as HAlignType,
      valign: 'middle' as VAlignType,
      overflow: 'hidden',
      cellWidth: 'wrap',
      ...(rowBorder ?? {}),
      cellPadding: {
        top:
          parseFloat(styles.paddingTop) < 2
            ? parseFloat(styles.paddingTop) * scale
            : 2 * scale,
        right:
          parseFloat(styles.paddingRight) < 2
            ? parseFloat(styles.paddingRight) * scale
            : 2 * scale,
        left:
          parseFloat(styles.paddingLeft) < 2
            ? parseFloat(styles.paddingLeft) * scale
            : 2 * scale,
      },
    };
  }

  private resolveRowBackgroundColor(cell: HTMLElement): string | undefined {
    const row = cell.closest('.pdf-print-row') as HTMLElement | null;
    if (!row) return;

    const bg = window.getComputedStyle(row).backgroundColor;
    if (!bg || bg === 'rgba(0, 0, 0, 0)') return;

    return bg;
  }
  private resolveRowBorder(cell: HTMLElement):
    | {
        lineWidth?: number;
        lineColor?: [number, number, number];
      }
    | undefined {
    const row = cell.closest('.pdf-print-row') as HTMLElement;
    if (!row) return;

    const styles = window.getComputedStyle(row);
    const borderBottom = styles.borderBottom;
    if (!borderBottom || borderBottom === 'none') return;

    const lineWidth = 0.2;
    const lineColor: [number, number, number] = [
      Number(150),
      Number(150),
      Number(150),
    ];
    return { lineWidth, lineColor };
  }

  chartCanvasToImage(canvas: HTMLCanvasElement): string {
    const dpr = window.devicePixelRatio || 1;

    const rect = canvas.getBoundingClientRect();

    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = rect.width * dpr;
    exportCanvas.height = rect.height * dpr;

    const ctx = exportCanvas.getContext('2d')!;
    ctx.scale(dpr, dpr);

    ctx.drawImage(canvas, 0, 0, rect.width, rect.height);

    return exportCanvas.toDataURL('image/png', 1.0);
  }
}
